// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useLocales } from "react-localized"
import {
  IonContent,
  IonHeader,
  IonMenu,
  IonTitle,
  IonToolbar
} from "@ionic/react"
import {
  faAddressBook,
  faBarcode,
  faBox,
  faCartPlus,
  faCloud,
  faGears,
  faHouse,
  faKey,
  faPallet,
  faReceipt,
  faStore, 
  faTruckRampBox,
  faUserShield,
  faWandSparkles}
from "@fortawesome/free-solid-svg-icons"
import { useRef } from "react"
import { MainMenuGroup, MainMenuItem, useSession } from "../../../lib"
import { constructUrl } from "../lib"
import { useAccount } from "../context"
import { ConditionalFeature } from "./ConditionalFeature"


const params: URLSearchParams = new URLSearchParams(window.location.search)
const accountId: number | null = (!!params.get("rlm"))
  ? parseInt(params.get("rlm") as string)
  : null
if (accountId !== null) {
  localStorage.setItem("Account.id", accountId.toString())
}


const ConsoleMainMenu: React.FC = () => {
  const {gettext} = useLocales()
  const session = useSession()
  const {activeAccount, permissions} = useAccount()
  const ref = useRef<HTMLIonMenuElement>(null)
  const closeMenu: () => void = () => {
    if (ref.current !== null) {
      ref.current.close()
    }
  }

  if (activeAccount === null || permissions === null) {
    return null
  }
  return (
    <IonMenu ref={ref} contentId="main-content" disabled={!activeAccount}>
      {(activeAccount === null || permissions === null) ? null : (
      <>
        <IonHeader className="main">
          <IonToolbar>
            <IonTitle>Molano Cloud Console</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <MainMenuGroup>
            <MainMenuItem
              icon={faHouse}
              onClick={closeMenu}
              title={gettext("Dashboard")}
              path={constructUrl("/home/dashboard")}
            />
            <MainMenuItem
              icon={faWandSparkles}
              onClick={closeMenu}
              title={gettext("Setup assistant")}
              path={constructUrl("/setup")}
              visible={permissions.has("accounts.accounts.configure")}
            />
          </MainMenuGroup>
          <MainMenuGroup title={gettext("Pinned")}>
          </MainMenuGroup>
          <MainMenuGroup
            title={gettext("Product management")}
            visible={permissions.has("pimp.*") && activeAccount.canUse("ALPHA")}
          >
            <MainMenuItem
              icon={faBox}
              onClick={closeMenu}
              title={gettext("Products")}
              path={constructUrl("/products/mdm")}
              visible={permissions.has("pimp.products.*") && activeAccount.canUse("ALPHA")}
            />
          </MainMenuGroup>
          <MainMenuGroup title={gettext("Procurement")} visible={activeAccount.canUse("ALPHA")}>
            <ConditionalFeature
              account={activeAccount}
              anyOf={["procurement.*"]}
              available={permissions}
              stage="ALPHA"
            >
              <MainMenuItem
                icon={faAddressBook}
                onClick={closeMenu}
                title={gettext("Suppliers")}
                path={constructUrl("/procurement/suppliers")}
                visible={permissions.has("procurement.suppliers.*")}
              />
              <MainMenuItem
                icon={faCartPlus}
                onClick={closeMenu}
                title={gettext("Purchase orders")}
                path={constructUrl("/procurement/orders")}
                visible={permissions.has("procurement.purchaseorders.*")}
              />
              <MainMenuItem
                icon={faStore}
                onClick={closeMenu}
                title={gettext("Marketplace")}
                path={constructUrl("/procurement/marketplace")}
                visible={permissions.has("procurement.marketplace.*") && activeAccount.canUse("ALPHA")}
              />
            </ConditionalFeature>
          </MainMenuGroup>
          <MainMenuGroup
            title={gettext("Logistics")}
            visible={permissions.has("warehouse.*") && activeAccount.canUse("ALPHA")}
          >
            <MainMenuItem
              icon={faTruckRampBox}
              onClick={closeMenu}
              title={gettext("Shipments")}
              path={constructUrl("/logistics/shipments")}
              visible={
                permissions.has("warehouse.shipments.*")
                && activeAccount.canUse("ALPHA")
              }
            />
            <MainMenuItem
              icon={faGears}
              onClick={closeMenu}
              title={gettext("Warehouse setup")}
              path={constructUrl("/logistics/setup")}
              visible={
                (
                  permissions.has("warehouse.containers.*")
                  && permissions.has("warehouse.locations.*")
                )
                && activeAccount.canUse("ALPHA")
              }
            />
          </MainMenuGroup>
          <MainMenuGroup
            title={gettext("Inventory")}
            visible={permissions.has("warehouse.*")}
          >
            <MainMenuItem
              icon={faBarcode}
              onClick={closeMenu}
              title={gettext("SKU Editor")}
              path={constructUrl("/inventory/sku")}
              visible={permissions.has("warehouse.sku.*")}
            />
            <MainMenuItem
              icon={faPallet}
              onClick={closeMenu}
              title={gettext("Register goods")}
              path={constructUrl("/inventory/register")}
              visible={
                permissions.has("warehouse.shipments.receive")
                && activeAccount.canUse("ALPHA")
              }
            />
          </MainMenuGroup>
          <MainMenuGroup
            title={gettext("Integration services")}
            visible={
              permissions.has("cip.integrations.*")
              && activeAccount.canUse("ALPHA")
            }
          >
            <MainMenuItem
              icon={faCloud}
              onClick={closeMenu}
              title={gettext("Molano Uplink")}
              path={constructUrl("/integrations/uplink")}
              visible={
                permissions.has("cip.integrations.*")
                && activeAccount.canUse("ALPHA")
              }
            />
          </MainMenuGroup>
          <MainMenuGroup
            title={gettext("Account")}
            visible={
              permissions.has("accounts.accounts.*IamPolicy")
              && activeAccount.canUse("ALPHA")
            }
          >
            <MainMenuItem
              icon={faUserShield}
              onClick={closeMenu}
              title={gettext("IAM & Security")}
              path={constructUrl("/account/iam")}
              visible={
                permissions.has("accounts.accounts.*IamPolicy")
                && activeAccount.canUse("ALPHA")
              }
            />
            <MainMenuItem
              icon={faKey}
              onClick={closeMenu}
              title={gettext("Service Credentials")}
              path={constructUrl("/accounts/credentials")}
              visible={
                permissions.has("accounts.credentials.*")
                && activeAccount.canUse("ALPHA")
              }
            />
            <MainMenuItem
              icon={faReceipt}
              onClick={closeMenu}
              title={gettext("Billing")}
              path={constructUrl("/account/billing")}
              visible={
                permissions.has("accounts.accounts.*IamPolicy")
                && activeAccount.canUse("ALPHA")
              }
            />
          </MainMenuGroup>
        </IonContent>
      </>

      )}
    </IonMenu>
  )
}


export { ConsoleMainMenu }